import React, { useState, useEffect, useRef } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormControl from "@mui/joy/FormControl";
import RadioGroup from "@mui/joy/RadioGroup";
import Radio from "@mui/joy/Radio";
import toast, { Toaster } from "react-hot-toast";
import CircularProgress from "@mui/material/CircularProgress";
import { api } from "../../utils/axios-instance";
import { auth } from "../../helper/firebaseClient";
import { useDepartment } from "../../stores/departmentStore";
import useAuth from "../../stores/authStore";

// images from public folder
const Inviteicon = `${process.env.PUBLIC_URL}/assets/InviteMember.webp`;

const InviteMember = (props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    emailAddress: "",
    role: "",
    department: "",
  });
  const [roles, setRoles] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [openDeptDropdown, setOpenDeptDropdown] = useState(false);
  const departments = useDepartment((state) => state.departments);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  const openDialog = () => {
    setIsOpen(true);
  };

  const closeDialog = () => {
    setIsOpen(false);
    setOpenDeptDropdown(false);
  };

  // Close dropdown on outside click
  useEffect(() => {
    if (!openDeptDropdown) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDeptDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openDeptDropdown]);

  useEffect(() => {
    const getRoles = async () => {
      try {
        const role = await api.get(`/role/listRoles`);
        console.log("roles:", role.data);
        setRoles(role.data);
      } catch (error) {
        console.log("Error fetching departments");
      }
    };

    getRoles();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleRadioChange = (e) => {
    const roleId = e.target.value;
    const roleObj = roles.find((r) => r.id === roleId);
    const roleName = roleObj?.role;

    setFormData({
      ...formData,
      role: roleId,
    });

    if (roleName === "super_admin") {
      // Select ALL departments and LOCK the dropdown — not openable
      setSelectedDepartments(departments.map((d) => d.name));
      setOpenDeptDropdown(false);
    } else {
      // employee: always CLEAR departments and AUTO-OPEN dropdown to force selection
      setSelectedDepartments([]);
      setOpenDeptDropdown(true);
    }
  };

  const handleDepartmentToggle = (deptName) => {
    // Guard: locked for super_admin
    if (isSuperAdmin) return;
    setSelectedDepartments((prev) =>
      prev.includes(deptName)
        ? prev.filter((d) => d !== deptName)
        : [...prev, deptName]
    );
  };

  // Derived role state
  const selectedRoleObj = roles.find((r) => r.id === formData.role);
  const isSuperAdmin = selectedRoleObj?.role === "super_admin";

  const handleInvite = async () => {
    try {
      setLoading(true);

      const user = auth.currentUser;
      if (!user) {
        toast.error("User not authenticated");
        return;
      }

      if (!formData.role) {
        toast.error("Please select a role");
        return;
      }

      if (!selectedDepartments.length) {
        toast.error("Please select at least one department");
        return;
      }

      const idToken = await user.getIdToken();
      const profile = useAuth.getState().profileData;
      const orgId = profile?.org_id;

      if (!orgId) {
        console.error("ORG ID MISSING", profile);
        toast.error("Organization not found");
        return;
      }

      const roleName =
        selectedRoleObj?.role === "org_admin"
          ? "employee"
          : selectedRoleObj?.role || "employee";

      // CORRECT PAYLOAD
      const body = {
        idToken: idToken,
        invites: [
          {
            email: formData.emailAddress.trim().toLowerCase(),
            role: roleName, // "employee" | "super_admin"
            dept_names: selectedDepartments,
          },
        ],
      };

      console.log("INVITE PAYLOAD:", body);

      const res = await api.post(`/invite`, body);

      if (res?.data) {
        toast.success("User invited successfully.");
        closeDialog();

        // Reset form
        setFormData({
          firstName: "",
          lastName: "",
          emailAddress: "",
          role: "",
        });
        setSelectedDepartments([]);
      }
    } catch (error) {
      console.error("Invite error:", error?.response?.data || error);

      const msg =
        error?.response?.data?.error ||
        "Failed to send invite. Check seats or input.";

      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="">
      <Toaster position="bottom-left" reverseOrder={false} />
      <div
        onClick={openDialog}
        className="w-[140px] h-[120px] py-4 px-3 rounded-lg border bg-[#f7f7ff] flex flex-col items-center justify-center gap-2 hover:bg-indigo-100 text-center cursor-pointer"
      >
        <img
          src={Inviteicon}
          alt="invite memeber"
          className="h-8 w-8 object-contain"
        />
        <p className="text-sm font-medium leading-tight">Invite a Member</p>
      </div>

      <Dialog
        open={isOpen}
        onClose={closeDialog}
        PaperProps={{
          style: {
            borderRadius: "5px",
          },
        }}
      >
        <DialogTitle>Invite a Member</DialogTitle>
        <DialogContent
          style={{
            backgroundColor: "#F7F8FA",
          }}
        >
          <div className="my-2">
            <div className="flex flex-row justify-between items-center gap-4">
              <span className="">
                <p className="my-1">
                  First Name<span className="text-[#5E5ADB]"> *</span>
                </p>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  className="border rounded-md w-56 px-2 py-1 shadow"
                />
              </span>
              <span>
                <p className="my-1">
                  Last Name<span className="text-[#5E5ADB]"> *</span>
                </p>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  className="border rounded-md w-56 px-2 py-1 shadow"
                />
              </span>
            </div>
            <span className="">
              <p className="my-1">
                Email Address<span className="text-[#5E5ADB]"> *</span>
              </p>
              <input
                type="text"
                name="emailAddress"
                value={formData.emailAddress}
                onChange={handleInputChange}
                required
                className="border rounded-md w-full px-2 py-1 shadow"
              />
            </span>
            <div className="w-[446px] my-4">
              <RadioGroup
                name="role"
                sx={{ gap: 1, "& > div": { p: 1 } }}
                value={formData.role || ""}
                onChange={handleRadioChange}
              >
                {roles.length ? (
                  roles
                    .filter((r) => r.role !== "org_admin") // BLOCK THIS ROLE
                    .map((role) => (
                      <FormControl size="sm" key={role.id}>
                        <Radio
                          overlay
                          value={role.id}
                          label={role.role}
                          sx={{ fontWeight: "bold", marginBottom: "3px" }}
                        />
                      </FormControl>
                    ))
                ) : (
                  <p>Loading the roles...</p>
                )}
              </RadioGroup>
            </div>

            {/* Department Multi-Select */}
            <div className="mt-3">
              <p className="my-1">
                Departments<span className="text-[#5E5ADB]"> *</span>
              </p>

              <div className="relative" ref={dropdownRef}>
                {/* ── LOCKED display for super_admin ── */}
                {isSuperAdmin ? (
                  <div className="w-full flex items-center px-3 py-2 border border-indigo-300 rounded-lg bg-indigo-50 text-sm cursor-not-allowed select-none">
                    <span className="bg-indigo-100 text-indigo-700 text-xs px-2 py-1 rounded">
                      All Departments ✓
                    </span>
                  </div>
                ) : (
                  /* ── INTERACTIVE dropdown for employee ── */
                  <>
                    <button
                      type="button"
                      onClick={() => setOpenDeptDropdown((prev) => !prev)}
                      className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-white hover:border-[#5E5ADB] text-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#5E5ADB]"
                    >
                      {/* Chips or placeholder */}
                      <div className="flex flex-wrap gap-1 flex-1 min-w-0">
                        {selectedDepartments.length === 0 ? (
                          <span className="text-gray-400">
                            Select departments
                          </span>
                        ) : (
                          selectedDepartments.map((name) => (
                            <span
                              key={name}
                              className="bg-indigo-100 text-indigo-700 text-xs px-2 py-1 rounded"
                            >
                              {name}
                            </span>
                          ))
                        )}
                      </div>

                      {/* Clear + chevron */}
                      <div className="flex items-center gap-1 ml-2 shrink-0">
                        {selectedDepartments.length > 0 && (
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedDepartments([]);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.stopPropagation();
                                setSelectedDepartments([]);
                              }
                            }}
                            className="text-gray-400 hover:text-gray-600 text-base leading-none cursor-pointer"
                            title="Clear all"
                          >
                            ✕
                          </span>
                        )}
                        <span className="text-gray-400 text-xs">
                          {openDeptDropdown ? "▴" : "▾"}
                        </span>
                      </div>
                    </button>

                    {/* Checkbox list */}
                    {openDeptDropdown && (
                      <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
                        {departments.map((dept) => {
                          const checked = selectedDepartments.includes(
                            dept.name
                          );
                          return (
                            <label
                              key={dept.id ?? dept.name}
                              className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() =>
                                  handleDepartmentToggle(dept.name)
                                }
                                className="accent-[#5E5ADB]"
                              />
                              <span>
                                {dept.emoji && `${dept.emoji} `}
                                {dept.name}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Hint text */}
              {formData.role && (
                <p className="text-xs text-gray-500 mt-1">
                  {isSuperAdmin
                    ? "Super Admin has access to all departments."
                    : "Please select at least one department for this member."}
                </p>
              )}
            </div>
          </div>
        </DialogContent>

        <DialogActions sx={{ padding: "10px" }}>
          <button
            className="px-2 py-1 mx-2 rounded-lg shadow-sm border border-gray-300 hover:text-red-400"
            onClick={closeDialog}
            color="primary"
          >
            Cancel
          </button>
          <button
            className="flex items-center px-2 py-1 rounded-lg shadow-sm bg-[#5E5ADB] hover:bg-indigo-400 text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
            onClick={handleInvite}
            disabled={loading}
          >
            Invite
            {loading && (
              <CircularProgress
                style={{ color: "gray", marginLeft: 6 }}
                size={20}
              />
            )}
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default InviteMember;
